import React from "react";
import { Link } from "react-router-dom";
import { Check, Clock } from "lucide-react";
import Seo from "@/components/site/Seo";
import SectionHeading from "@/components/site/SectionHeading";
import { IMAGES, ACUITY, TRAINING_COURSES } from "@/data/site";
import { educationSchema, breadcrumb } from "@/data/schema";

const COURSES_HREF = `/book?category=${encodeURIComponent(ACUITY.coursesCategory)}`;

const HIGHLIGHTS = [
  "Hands-on training with an experienced lash & brow artist",
  "Learn professional technique, safety and sanitation",
  "Build the confidence to take on your own clients",
  "Guidance on growing a successful beauty business",
];

export default function Training() {
  return (
    <>
      <Seo
        title="Lash & Brow Training in Vero Beach, FL | Golden Touch Academy"
        description="Professional lash and brow training for aspiring artists in Vero Beach, Florida. Learn, perfect and elevate your craft with hands-on education from Golden Touch Lash & Brow."
        path="/training"
        image={IMAGES.training}
        jsonLd={[
          educationSchema(),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Training", path: "/training" },
          ]),
        ]}
      />

      <section className="relative bg-charcoal pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="reveal">
            <p className="eyebrow">Beauty. Education. Empowerment.</p>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05]">
              Learn. Perfect. Elevate.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-ivory">
              Turn your passion into a profession. Golden Touch offers professional training for
              aspiring lash & brow artists - blending precise technique with the business knowledge
              you need to thrive.
            </p>
            <List />
            <Link
              to={COURSES_HREF}
              data-testid="training-hero-book-btn"
              className="btn-gold mt-9 inline-flex px-8 py-3.5 text-[0.72rem] tracking-[0.24em] uppercase"
            >
              Book a Class
            </Link>
          </div>
          <div
            className="reveal relative flex items-end justify-center overflow-hidden"
            style={{ background: "radial-gradient(55% 60% at 60% 40%, rgba(128,114,96,0.40) 0%, rgba(13,13,13,0) 68%)" }}
          >
            <img
              src={IMAGES.training}
              alt="Golden Touch lash & brow educator"
              width={1122}
              height={1402}
              loading="lazy"
              decoding="async"
              className="mx-auto block h-auto w-auto max-w-full max-h-[620px] object-contain object-bottom"
            />
          </div>
        </div>
      </section>

      <section className="bg-onyx py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <SectionHeading dark>Classes You Can Book</SectionHeading>
          <p className="mx-auto mt-6 max-w-2xl text-muted-ivory leading-relaxed">
            Choose a class below and book your seat instantly - no inquiry needed. Every class is
            hands-on and led personally by Golden Touch.
          </p>

          <div className="mt-12 grid gap-5 text-left sm:grid-cols-2">
            {TRAINING_COURSES.map((c) => (
              <article
                key={c.name}
                data-testid={`training-course-${c.name}`}
                className="flex h-full flex-col border border-gold/25 bg-charcoal p-6 sm:p-7"
              >
                <p className="text-[0.6rem] uppercase tracking-[0.26em] text-gold">{c.level}</p>
                <h3 className="mt-3 font-display text-xl text-ivory sm:text-2xl">{c.name}</h3>
                <div className="mt-3 flex items-center gap-4 text-xs uppercase tracking-[0.14em] text-muted-ivory">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={13} className="text-gold" /> {c.duration}
                  </span>
                  <span className="text-gold">{c.price}</span>
                </div>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-ivory">{c.desc}</p>
                <Link
                  to={COURSES_HREF}
                  data-testid={`training-course-book-${c.name}`}
                  className="btn-gold-outline mt-6 inline-flex w-full justify-center px-6 py-3 text-[0.68rem] tracking-[0.22em] uppercase"
                >
                  Book This Class
                </Link>
              </article>
            ))}
          </div>

          <Link
            to={COURSES_HREF}
            data-testid="training-book-all-btn"
            className="btn-gold mt-12 inline-flex px-9 py-3.5 text-[0.72rem] tracking-[0.24em] uppercase"
          >
            View &amp; Book All Classes
          </Link>
        </div>
      </section>
    </>
  );
}

function List() {
  return (
    <ul className="mt-8 space-y-3">
      {HIGHLIGHTS.map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-muted-ivory">
          <Check size={18} className="mt-0.5 shrink-0 text-gold" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}
